/**
 * Mixin for document numbering validation
 *
 * Usage in Create.vue or Edit.vue:
 *
 * import documentNumberingValidation from '@/mixins/documentNumberingValidation'
 *
 * mixins: [FormsMixin, documentNumberingValidation],
 *
 * props: {
 *     modelDef: String,  // e.g., 'documents-saleinvoice', 'quotations', 'ddts'
 *     ...
 * },
 *
 * The mixin will automatically check chronology before save if:
 * - model has 'number', 'date' (or 'quotation_date'), and 'sectional_id'
 * - settings allow the check
 */

export default {
    data() {
        return {
            chronologyDialog: false,
            chronologyConflicts: null,
            chronologyCheckPending: false,
            chronologyCallback: null
        }
    },

    methods: {
        /**
         * Check chronological progression before save
         * Call this in your createModel() or updateModel() method
         *
         * @param {Function} callback - Function to call if check passes or user confirms
         * @returns {Promise<boolean>} - true if should proceed, false if should wait for user
         */
        async checkChronologyBeforeSave(callback) {
            // Extract model type from modelDef
            const modelType = this.extractModelType(this.modelDef)

            if (!modelType) {
                // If we can't determine model type, proceed
                callback()
                return true
            }

            // Check if chronology check is enabled for this model type from settings
            const isChronologyEnabled = this.$store.getters.getSettingByModel(modelType, 'enable_cronology_check')

            // If not enabled, proceed without check
            if (!(isChronologyEnabled === true || isChronologyEnabled === 'true' || isChronologyEnabled === 1)) {
                callback()
                return true
            }

            // Check if model has required fields
            if (!this.model.number || !this.model.sectional_id) {
                // No number or sectional, can't check
                callback()
                return true
            }

            // Get date field
            const dateField = modelType === 'quotations' ? 'quotation_date' : 'date'
            const date = this.model[dateField]

            if (!date) {
                // No date, can't check
                callback()
                return true
            }

            // Perform chronology check
            try {
                this.chronologyCheckPending = true

                const result = await this.$store.dispatch('CHECK_CHRONOLOGY', {
                    modelType: modelType,
                    number: this.model.number,
                    date: date,
                    sectionalId: this.model.sectional_id,
                    excludeId: this.model.id || null
                })

                this.chronologyCheckPending = false

                if (!result || !result.enabled) {
                    // Check disabled or failed, proceed
                    callback()
                    return true
                }

                if (result.valid) {
                    // No conflicts, proceed
                    callback()
                    return true
                }

                // Conflicts found, show dialog
                this.chronologyConflicts = result.conflicts
                this.chronologyCallback = callback
                this.chronologyDialog = true

                return false // Wait for user confirmation

            } catch (error) {
                console.error('Error checking chronology:', error)
                this.chronologyCheckPending = false
                // On error, proceed anyway
                callback()
                return true
            }
        },

        /**
         * Handle chronology confirmation
         */
        handleChronologyConfirm() {
            if (this.chronologyCallback) {
                this.chronologyCallback()
                this.chronologyCallback = null
            }
            this.chronologyDialog = false
            this.chronologyConflicts = null
        },

        /**
         * Handle chronology cancellation
         */
        handleChronologyCancel() {
            // Create/Edit bloccano il pulsante dal primo clic: annullando il salvataggio va sbloccato
            this.saved = false
            this.chronologyCallback = null
            this.chronologyDialog = false
            this.chronologyConflicts = null
        },

        /**
         * Extract model type from modelDef
         *
         * Examples:
         * - 'documents-saleinvoice' => 'saleinvoice'
         * - 'quotations' => 'quotations'
         * - 'ddts' => 'ddt'
         *
         * @param {String} modelDef
         * @returns {String|null}
         */
        extractModelType(modelDef) {
            if (!modelDef) return null

            // Handle documents-{type} pattern
            if (modelDef.startsWith('documents-')) {
                return modelDef.replace('documents-', '')
            }

            // Handle plural forms
            const mapping = {
                'quotations': 'quotation',
                'ddts': 'ddt',
                'saleorders': 'saleorder'
            }

            return mapping[modelDef] || modelDef
        }
    }
}
